/**
 * The tablet's copy of what it works from with no data: its robot, its hospital's wards and
 * operators, and the beds cleaned. Kept up to date with only what changed since the last sync
 * (GET /tablets/current/changes); a full copy the first time, after a robot change, and once a day.
 */
import type {
  OfflinePackResponse,
  OfflineSettingsDto,
  PackChangesResponse,
  SanitisedBedDto,
} from '@evesix/contracts';

export interface TabletPack extends OfflineSettingsDto {
  readonly robot: OfflinePackResponse['robot'];
  readonly wards: OfflinePackResponse['wards'];
  readonly operators: OfflinePackResponse['operators'];
  /** Bed key ("2A-03") → when it was last cleaned; aged out by the sanitised window. */
  readonly sanitised: Readonly<Record<string, string>>;
  /** Sent back as `since`; null until the first sync of this kind. */
  readonly cursor: string | null;
  /** When the last full copy arrived (epoch ms). */
  readonly fullAt: number;
}

/** A full copy at least this often, so anything a change log could miss is put right. */
export const FULL_REFRESH_MS = 24 * 60 * 60 * 1000;

function cleaned(
  base: Readonly<Record<string, string>>,
  beds: readonly SanitisedBedDto[],
  keepAfter: number,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [bed, at] of Object.entries(base)) if (Date.parse(at) > keepAfter) next[bed] = at;
  for (const { bed, at } of beds) {
    const known = next[bed];
    if (Date.parse(at) > keepAfter && (!known || known < at)) next[bed] = at;
  }
  return next;
}

/** Replaces items with the same key, adds new ones at the end, drops the removed. */
function merged<T, K extends string | number>(
  list: readonly T[],
  changed: readonly T[],
  removed: readonly K[],
  key: (item: T) => K,
): T[] {
  const updates = new Map(changed.map((item) => [key(item), item]));
  const gone = new Set(removed);
  const next = list
    .filter((item) => !gone.has(key(item)))
    .map((item) => updates.get(key(item)) ?? item);
  const known = new Set(next.map(key));
  return [...next, ...changed.filter((item) => !known.has(key(item)))];
}

/** The copy after a sync: `base` with the changes, or the full copy that came instead. */
export function applyChanges(
  base: TabletPack | null,
  changes: PackChangesResponse,
  now: number,
): TabletPack {
  if (changes.full || !base) {
    if (!changes.robot || !changes.settings) throw new Error('A full copy came without its robot');
    const keepAfter = now - 2 * changes.settings.sanitisedWindowSeconds * 1000;
    return {
      ...changes.settings,
      robot: changes.robot,
      wards: changes.wards,
      operators: changes.operators,
      sanitised: cleaned({}, changes.sanitised, keepAfter),
      cursor: changes.cursor,
      fullAt: now,
    };
  }
  return {
    ...base,
    wards: merged(base.wards, changes.wards, changes.removedWards, (w) => w.code),
    operators: merged(base.operators, changes.operators, changes.removedOperators, (o) => o.id),
    sanitised: cleaned(
      base.sanitised,
      changes.sanitised,
      now - 2 * base.sanitisedWindowSeconds * 1000,
    ),
    cursor: changes.cursor,
  };
}

/** A whole pack (from GET /tablets/current/pack, or saved by app 0.3) as the tablet keeps it. */
export function fromFullPack(pack: OfflinePackResponse, now: number): TabletPack {
  const at = pack.generatedAt;
  return {
    robot: pack.robot,
    wards: pack.wards,
    operators: pack.operators,
    sanitised: Object.fromEntries(pack.sanitisedBeds.map((bed) => [bed, at])),
    sanitisedWindowSeconds: pack.sanitisedWindowSeconds,
    lockout: pack.lockout,
    sessionTtlSeconds: pack.sessionTtlSeconds,
    manualLampMaxSeconds: pack.manualLampMaxSeconds,
    cursor: null, // the next sync asks for a full copy
    fullAt: now,
  };
}

/** Whether the next sync should ask for everything rather than the changes. */
export function needsFullCopy(pack: TabletPack | null, now: number): boolean {
  return !pack?.cursor || now - pack.fullAt >= FULL_REFRESH_MS;
}
